import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import { CreateChancel, NotError } from 'components';
import { SignaturePanel } from '../common/signaturePanel';
import {
    FindLoad,
    base64ToFile,
    FindSuccessItem,
    formatAMPMNeW,
    Images,
    useModal,
    KeyPurpose,
    User,
    RolePermission, PermissionList
} from 'utils';
import {adminActions, appointmentActions, clientActions, httpRequestsOnSuccessActions, uploadFile} from 'store';
import { bulkSignTargetEnum, signatureTypeEnums } from './constants';
import {GuardianSelector} from "../common/guardianSelector";
import { buildSignerPayload, isOtherSignerIncomplete } from '../common/constants';
import {useLocation} from "react-router-dom";

const EXISTING_SIGNATURE_MODE = {
    SKIP: 'SKIP',
    REPLACE: 'REPLACE',
};

const SIGNATURE_TAB = {
    DRAW: 'Draw',
    TYPE: 'Type',
};

const BULK_ASSIGN = 'BULK_ASSIGN_SIGNATURE';

export const BulkAssignSignature = ({ selectAllList, selectedFullList, afterSuccess, signatureType = bulkSignTargetEnum.PROVIDER }) => {
    const dispatch = useDispatch();
    const { close } = useModal();
    const { userInfo } = User();
    const geoPos = useSelector((state) => state.appointment.geoPos);
    const isGuardian = signatureType === bulkSignTargetEnum.GUARDIAN;
    const signerLabel = isGuardian ? 'Guardian' : 'Provider';
    const [expanded, setExpanded] = useState(true);
    const [existingMode, setExistingMode] = useState(EXISTING_SIGNATURE_MODE.SKIP);
    const [loading, setLoading] = useState(false);
    const [typedName, setTypedName] = useState('');
    const [selectedGuardian, setSelectedGuardian] = useState('');
    const [otherSigner, setOtherSigner] = useState(null);
    const [showSignerErrors, setShowSignerErrors] = useState(false);
    const canvasRef = useRef(null);
    const signaturePanelRef = useRef(null);
    const apptsWithSignature = selectedFullList?.filter((a) => a?.signatureUrl) || [];
    const apptsWithoutSignature = selectedFullList?.filter((a) => !a?.signatureUrl) || [];
    const applicableAppts = existingMode === EXISTING_SIGNATURE_MODE.SKIP ? apptsWithoutSignature : selectedFullList || [];
    const skippedCount = (selectedFullList?.length || 0) - applicableAppts.length;
    const success = FindSuccessItem(BULK_ASSIGN);
    const loadBulk = FindLoad(BULK_ASSIGN);
    const location = useLocation();
    const info = location?.state;

    useEffect(() => {
        if (info?.clients?.length === 1 && isGuardian) {
            if (RolePermission([PermissionList.CLIENT_CONTACT_READ?.code])) {
                dispatch(clientActions.getClientsContacts(info?.clients[0]));
            } else {
                dispatch(clientActions.getClientsContactInfo(info?.clients[0]));
            }
        }
    }, [info]);

    useEffect(() => {
        if(success){
            afterSuccess?.();
            dispatch(httpRequestsOnSuccessActions.removeSuccess(BULK_ASSIGN));
            close();
        }
    }, [success]);

    const generateTypedSignature = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 480;
        canvas.height = 120;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.font = "italic 40px 'Dancing Script', 'Brush Script MT', cursive";
        ctx.fillStyle = '#172B4D';
        ctx.textBaseline = 'middle';
        ctx.fillText(typedName, 20, 60);
        return canvas.toDataURL('image/png');
    };

    const handleApply = async () => {
        if (!applicableAppts.length || loading) return;
        if (isGuardian && isOtherSignerIncomplete(otherSigner)) {
            setShowSignerErrors(true);
            return;
        }

        const panelState = signaturePanelRef.current;
        const fileName = isGuardian ? 'guardian-signature' : 'provider-signature'
        setLoading(true);
        try {
            let fileData = null;
            let convertedSignatureFile = null;

            if (panelState?.useSaved && panelState?.savedSignature?.key) {
                fileData = panelState.savedSignature;
            } else if (panelState?.activeTab === SIGNATURE_TAB.DRAW) {
                if (!canvasRef.current?.current) return;
                const signatureData = canvasRef.current.current.getTrimmedCanvas().toDataURL('image/png');
                convertedSignatureFile = base64ToFile(signatureData, `${fileName}.png`);
                fileData = await uploadFile(convertedSignatureFile, KeyPurpose.SIGNATURE);
            } else if (panelState?.activeTab === SIGNATURE_TAB.TYPE) {
                if (!typedName.trim()) return;
                const signatureData = generateTypedSignature();
                convertedSignatureFile = base64ToFile(signatureData, `${fileName}.png`);
                fileData = await uploadFile(convertedSignatureFile, KeyPurpose.SIGNATURE);
            }

            if (!fileData) throw new Error('No signature data');

            if (panelState?.saveAsDefault && userInfo?.id && convertedSignatureFile) {
                const storedSignatureFile = await uploadFile(convertedSignatureFile, KeyPurpose.SIGNATURE);
                dispatch(adminActions.createStaffSignature({ file: { ...storedSignatureFile, name: 'staffSignature' } }, userInfo.id));
            }

            const apptIds = applicableAppts.map((appt) => appt.id);

            const body = {
                apptIds: apptIds,
                file: {
                    ...fileData,
                    name: fileName,
                },
                signatureType: isGuardian ? signatureTypeEnums.SIGNATURE : signatureTypeEnums.PROVIDER_SIGNATURE,
                signatureLocation: geoPos ? { lat: geoPos.lat, lng: geoPos.lng } : null,
            };
            if (isGuardian) Object.assign(body, buildSignerPayload(selectedGuardian, otherSigner));

            dispatch(appointmentActions.bulkAssignSignature(body));

        } catch (e) {
            const errorMessage = e?.response?.data?.message || e?.data?.message || e?.message || 'Signature upload failed';
            toast(<NotError type="error" errMessage={errorMessage} />);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bulk-sign-modal">
            {/* Header */}
            <p className="modal-header-title">{`Apply ${signerLabel} Signature to ${
                selectAllList ? 'All' : selectedFullList?.length
            } Appointments`}</p>

            {/* Summary */}
            <div className="bulk-sign-summary">
                <div className={`bulk-sign-selected ${expanded ? 'expanded' : ''}`} onClick={() => setExpanded(!expanded)}>
                    <span>
                        <b>Selected:</b> {selectAllList ? 'all' : selectedFullList?.length} appointments
                    </span>
                    <img src={Images.arrowDown} alt="toggle" />
                </div>
                <p className="bulk-sign-apply-info">
                    <b>Will apply to:</b> {applicableAppts.length} of {selectedFullList?.length} appointments
                    {skippedCount > 0 && <span> ({skippedCount} skipped)</span>}
                </p>
            </div>

            {/* Appointments list */}
            {expanded && (
                <div className="bulk-sign-appts-list">
                    {selectedFullList?.map((appt) => {
                        const hasSig = !!appt?.signatureUrl;
                        const isSkipped = existingMode === EXISTING_SIGNATURE_MODE.SKIP && hasSig;
                        return (
                            <div key={appt?.id} className={`bulk-sign-appt-row ${isSkipped ? 'skipped' : ''}`}>
                                <div className="appt-row-left">
                                    <span className="appt-display-id">{appt?.displayId}</span>
                                    <span className="appt-client-name">{appt?.client?.fullName || '--'}</span>
                                    <span className="appt-date">
                                        {moment.utc(appt?.startDate).format('M/D/YY')},{' '}
                                        {`${formatAMPMNeW(appt?.startTime)}-${formatAMPMNeW(appt?.endTime)}`}
                                    </span>
                                </div>
                                <div className="appt-row-right">
                                    {hasSig ? (
                                        <span className="appt-has-signature">Has signature · Skip</span>
                                    ) : (
                                        <span className="appt-ready">Ready</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Warning for existing signatures */}
            {apptsWithSignature.length > 0 && (
                <div className="bulk-sign-warning-section">
                    <div className="bulk-sign-warning">
                        <img src={Images.alertWarning} alt="warning" />
                        <p>
                            {apptsWithSignature.length} appointment{apptsWithSignature.length !== 1 ? 's' : ''} already contain
                            {apptsWithSignature.length === 1 ? 's' : ''} provider signature
                        </p>
                    </div>
                    <div className="bulk-sign-toggle">
                        <span className="toggle-label">For existing signatures:</span>
                        <div className="toggle-options">
                            <button
                                className={`toggle-option ${existingMode === EXISTING_SIGNATURE_MODE.SKIP ? 'active' : ''}`}
                                onClick={() => setExistingMode(EXISTING_SIGNATURE_MODE.SKIP)}>
                                Skip Existing
                            </button>
                            <button
                                className={`toggle-option ${existingMode === EXISTING_SIGNATURE_MODE.REPLACE ? 'active' : ''}`}
                                onClick={() => setExistingMode(EXISTING_SIGNATURE_MODE.REPLACE)}>
                                Replace Existing
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Provider Signature Section */}
            <div className="bulk-sign-signature-section">
                <div className="bulk-sign-signature-header">
                    <img src={Images.signIcon} alt="signature" />
                    <p className="bulk-sign-signature-title">{signerLabel} Signature</p>
                </div>

                <SignaturePanel
                    ref={signaturePanelRef}
                    appt={applicableAppts?.[0] || {}}
                    provider={!isGuardian}
                    showSavedSignature={!isGuardian}
                    onCanvasRef={(ref) => {
                        canvasRef.current = ref;
                    }}
                    onNameChange={setTypedName}
                />

                {isGuardian &&
                <GuardianSelector
                    setSelectedGuardian={setSelectedGuardian}
                    selectedGuardian={selectedGuardian}
                    otherSigner={otherSigner}
                    setOtherSigner={(value) => {
                        setShowSignerErrors(false);
                        setOtherSigner(value);
                    }}
                    showErrors={showSignerErrors}
                />
                }

            </div>

            <CreateChancel
                create={`Apply to ${applicableAppts.length} Appointment${applicableAppts.length !== 1 ? 's' : ''}`}
                chancel={'Cancel'}
                onCreate={handleApply}
                onClose={close}
                loader={loading || loadBulk?.length}
                buttonWidth={'100%'}
            />
        </div>
    );
};
